/* prettier-ignore-file */
/**
 * Builds and persists a booking document from the wizard draft.
 *
 * Produces the SAME document shape NewBooking.js writes (NewBooking.js:2395-2462)
 * so bookings created by the wizard are indistinguishable downstream — ViewBooking,
 * EditBooking, CheckIn, the cloud functions and the receipt templates all keep
 * working unchanged.
 *
 * NewBooking.js is intentionally left untouched, so this duplicates its write path
 * rather than refactoring it. The costs come from utils/rentalPricing.js.
 *
 * All money is stored in CENTS (.cursorrules #3).
 */

import { doc, setDoc, Timestamp } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db, app, auth } from "../firebase.config";
import { getCollectionPath } from "./dbPaths";
import { getTenantId, getStoreId } from "../services/userStoreCache";
import { useAuthStore } from "../stores";
import { computeRentalQuote, parseRentalDate } from "./rentalPricing";

const functions = getFunctions(app);

/** Customer-facing short id: last 5 digits of the long booking id. */
const shortBookingIdFromLong = (longBookingId) => {
  const digits = String(longBookingId || "").replace(/\D/g, "");
  return digits.slice(-5);
};

const generateSecurityPin = () => String(Math.floor(Math.random() * 900) + 100);

/**
 * Convert the wizard draft's store-shaped selections into booking line items.
 *
 * selectedBikes is keyed by product NAME (useNewBookingStore's convention),
 * selectedAddOns by product ID with `true` meaning a per-bike add-on.
 */
export const buildLineItems = ({
  selectedBikes,
  selectedAddOns,
  products1,
  products2,
  dailyRateOverrideCents = null,
}) => {
  const hasOverride =
    Number.isFinite(dailyRateOverrideCents) && dailyRateOverrideCents >= 0;

  const product1 = Object.entries(selectedBikes || {})
    .filter(([, quantity]) => quantity > 0)
    .map(([name, quantity]) => {
      const catalogProduct = (products1 || []).find((p) => p.name === name);
      return {
        // Mirrors NewBooking.js:2114 — fall back to the name when the lookup misses.
        id: catalogProduct?.id || catalogProduct?.productId || name,
        name,
        quantity,
        // pricePaid must be what was actually charged, not the catalog price, or
        // receipts and edit-time recalculation would disagree with the total taken.
        pricePaid: hasOverride ? dailyRateOverrideCents : catalogProduct?.price || 0,
      };
    });

  const totalBikes = product1.reduce((sum, item) => sum + item.quantity, 0);

  const product2 = Object.entries(selectedAddOns || {})
    .map(([addOnId, value]) => {
      const catalogProduct = (products2 || []).find((p) => p.id === addOnId);
      if (!catalogProduct) return null;
      const option = catalogProduct.option || "per-booking";
      const quantity = value === true ? 1 : value?.quantity || 0;
      if (quantity <= 0) return null;
      // per-bike add-ons store the TOTAL paid, not the unit price (NewBooking.js:2135).
      const pricePaid =
        option === "per-bike" ? (catalogProduct.price || 0) * totalBikes : catalogProduct.price || 0;
      return { id: addOnId, name: catalogProduct.name, quantity, option, pricePaid };
    })
    .filter(Boolean);

  return { product1, product2, totalBikes };
};

/**
 * A cash payment entry for the `payments` array.
 *
 * Mirrors the canonical entry the Stripe webhook writes (functions/index.js:3916)
 * with the Stripe identifiers nulled out. Two details are load-bearing:
 *
 *  - `costBreakdown` MUST be present. EditBooking.js:3924 and :4104 dereference
 *    `payments[0].costBreakdown.totalCharged` with no optional chaining, so an entry
 *    without it throws and breaks the edit page for that booking.
 *  - `amount` must equal `costBreakdown.totalCharged`. Bookings.js:742 prefers
 *    costBreakdown.totalCharged while ViewBooking reads `amount`; if they disagree the
 *    same booking shows two different paid figures.
 *
 * `type` stays "initial" because that is the only value ViewBooking.js:993 maps to a
 * friendly label — anything else is printed raw. The cash-ness lives in `method`.
 */
export const buildCashPaymentEntry = ({ quote, product1, product2 }) => ({
  paymentId: `cash_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
  type: "initial",
  method: "cash",
  timestamp: Timestamp.now(), // serverTimestamp() is illegal inside an array

  stripeCustomerId: null,
  stripeSessionId: null,
  stripePaymentIntentId: null,
  stripePaymentMethodId: null,
  cardInfo: null,

  amount: quote.finalTotalCents,
  currency: "usd",

  items: { product1, product2 },

  costBreakdown: {
    productCost: quote.productCostCents,
    deliveryCost: quote.deliveryCostCents,
    pickupCost: quote.pickupCostCents,
    deliveryWithDiscount: quote.deliveryWithDiscountCents,
    pickupWithDiscount: quote.pickupWithDiscountCents,
    durationDiscount: {
      discountPercent: quote.durationDiscountPercent,
      discountName: quote.durationDiscountName,
      discountCents: quote.durationDiscountCents,
    },
    deliveryDiscount: {
      discountPercent: quote.transportationDiscountPercent,
      discountName: quote.transportationDiscountName,
      discountCents: quote.transportationDiscountCents,
    },
    salesTaxCharged: quote.salesTaxCents,
    totalCharged: quote.finalTotalCents,
  },
});

/**
 * Where pending bookings live. getCollectionPath falls back to the default
 * unauthenticated tenant path (localTenantConfig) when there is no user context —
 * NOT to the root collection. That matters: every reader watches the tenant path
 * (the soft-hold subscription in pendingBookingsCache.js, the webhook's primary
 * lookup and its post-payment delete, StripeCheckout's waiver update,
 * PaymentSuccess's cleanup), so a root-written document would hold no inventory
 * against other shoppers and would never be cleaned up after payment.
 */
const resolvePendingBookingsPath = () => getCollectionPath("pendingBookings");

/**
 * Assemble the booking document (no writes).
 * @returns {{ bookingData: Object, quote: Object, bookingId: string }}
 */
export const buildWizardBookingData = ({ draft, products1, products2, settings }) => {
  const dailyRateOverrideCents = Number.isFinite(draft.dailyRateOverrideCents)
    ? draft.dailyRateOverrideCents
    : null;

  const { product1, product2 } = buildLineItems({
    selectedBikes: draft.selectedBikes,
    selectedAddOns: draft.selectedAddOns,
    products1,
    products2,
    dailyRateOverrideCents,
  });

  const quote = computeRentalQuote({
    startDate: draft.startDate,
    endDate: draft.endDate,
    product1,
    product2,
    products1Catalog: products1,
    products2Catalog: products2,
    settings,
    delivery: draft.deliveryNeeded,
    homePickup: draft.homePickupNeeded,
    miles: draft.miles,
    dailyRateOverrideCents,
  });

  const bookingId = `booking_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  const selectedUser = useAuthStore.getState().selectedUser;

  const notesWithCommunity = draft.gatedCommunity && draft.communityName
    ? `${draft.addressNotes || ""}${draft.addressNotes ? "\n" : ""}Gated community: ${draft.communityName}\n`
    : draft.addressNotes || "";

  const needsAddress = draft.deliveryNeeded || draft.homePickupNeeded;

  const bookingData = {
    bookingId,
    createdAt: Timestamp.now(),
    customerBookingID: shortBookingIdFromLong(bookingId),
    createdBy: {
      userId: selectedUser?.id || auth.currentUser?.uid || null,
      firstName: selectedUser?.firstName || "",
      lastName: selectedUser?.lastName || "",
      timestamp: Timestamp.now(),
    },

    customerEmail: (draft.customerEmail || "").trim().toLowerCase(),
    customerPhone: (draft.customerPhone || "").replace(/\D/g, ""),
    firstName: (draft.firstName || "").trim().toLowerCase(),
    lastName: (draft.lastName || "").trim().toLowerCase(),

    deliveryAddress: needsAddress && draft.addressInput ? draft.addressInput.trim() : "",
    delivery: !!draft.deliveryNeeded,
    homePickup: !!draft.homePickupNeeded,
    inStorePickup: !!draft.inStorePickup,
    gatedCommunity: !!draft.gatedCommunity,
    addressNotes: notesWithCommunity,
    numMiles: draft.miles != null ? parseFloat(Number(draft.miles).toFixed(1)) : null,

    startDate: Timestamp.fromDate(new Date(draft.startDate)),
    endDate: Timestamp.fromDate(new Date(draft.endDate)),

    adminNotes: draft.adminNotes || "",
    failedPinAttempts: 0,
    pinLocked: false,
    securityPin: generateSecurityPin(),

    product1,
    product2,
    payments: [],
    refunds: [],

    waiverSigned: !!draft.waiverSigned,
    waiverData: draft.waiverData
      ? [{ ...draft.waiverData, signedAt: Timestamp.now() }]
      : [],

    storeId: getStoreId() || "999",
    tenantId: getTenantId() || "1234",

    // --- Money, all cents ---
    // Recorded so the booking is auditable: without it, a later reader cannot tell a
    // negotiated rate from a catalog price change, and EditBooking would recompute
    // against catalog prices and silently reprice the booking.
    dailyRateOverride: dailyRateOverrideCents,

    salesTaxPercent: quote.salesTaxPercent,
    bookingPlatformFee: quote.platformFeeCents,
    productPriceAmount: quote.productCostCents,
    cardProcessingFeeAmount: quote.cardProcessingFeeCents,
    salesTaxAmount: quote.salesTaxCents,
    totalAmount: quote.finalTotalCents,
    deliveryCostCents: draft.deliveryNeeded ? quote.deliveryCostCents : 0,
    pickupCostCents: draft.homePickupNeeded ? quote.pickupCostCents : 0,
    deliveryWithDiscount: draft.deliveryNeeded ? quote.deliveryWithDiscountCents : 0,
    pickupWithDiscount: draft.homePickupNeeded ? quote.pickupWithDiscountCents : 0,
    transportationDiscount: {
      discountPercent: quote.transportationDiscountPercent,
      discountName: quote.transportationDiscountName,
      discountCents: quote.transportationDiscountCents,
    },
    durationDiscount: {
      discountPercent: quote.durationDiscountPercent,
      discountName: quote.durationDiscountName,
      discountCents: quote.durationDiscountCents,
    },
    tempCostBreakdown: {
      productCost: quote.productCostCents,
      deliveryCost: quote.deliveryCostCents,
      pickupCost: quote.pickupCostCents,
      durationDiscount: quote.durationDiscountCents,
      deliveryDiscount: quote.transportationDiscountCents,
      salesTaxCharged: quote.salesTaxCents,
      totalCharged: quote.finalTotalCents,
      deliveryWithDiscount: quote.deliveryWithDiscountCents,
      pickupWithDiscount: quote.pickupWithDiscountCents,
    },
  };

  return { bookingData, quote, bookingId };
};

/**
 * Build the booking, optionally persist it to pendingBookings, and create a
 * Stripe payment link.
 *
 * @param {boolean} saveToFirestore  false for the waiver flow, which saves later
 * @returns {{ paymentUrl, sessionId, bookingId, bookingData, pendingBookingsPath }}
 */
export const createWizardBooking = async ({
  draft,
  products1,
  products2,
  settings,
  useTestStripe,
  saveToFirestore = true,
  // Cash bookings take no card payment, so no Stripe link is created. They are marked
  // so an abandoned one is identifiable in pendingBookings rather than looking like a
  // half-finished card checkout.
  cashPayment = false,
}) => {
  const built = buildWizardBookingData({ draft, products1, products2, settings });
  const { quote, bookingId } = built;
  const bookingData = cashPayment
    ? { ...built.bookingData, paymentMethod: "cash", status: "awaiting_cash" }
    : built.bookingData;

  const pendingBookingsPath = resolvePendingBookingsPath();

  if (saveToFirestore) {
    await setDoc(doc(db, pendingBookingsPath, bookingId), bookingData);
  }

  // Mirror NewBooking.js:2503-2507 so the payment-success page can recover context.
  try {
    localStorage.setItem(
      "latestBooking",
      JSON.stringify({
        ...bookingData,
        createdAt: bookingData.createdAt.toDate().toISOString(),
        startDate: bookingData.startDate.toDate().toISOString(),
        endDate: bookingData.endDate.toDate().toISOString(),
        createdBy: { ...bookingData.createdBy, timestamp: bookingData.createdBy.timestamp.toDate().toISOString() },
      })
    );
    localStorage.setItem("latestBookingId", bookingId);
  } catch (error) {
    console.error("⚠️ Could not cache latest booking:", error);
  }

  // --- Stripe checkout labels -------------------------------------------------
  // This is the only description of the purchase the customer sees while paying,
  // so it names the actual bikes and dates. The old summary counted product1
  // ("1 item(s)") and named only product2, which left the rented bike anonymous.
  //
  // Dates use the same format StepDates.js:74 shows in the wizard, and are
  // formatted client-side, so the checkout page agrees with the calendar the
  // customer just picked from rather than with the server's timezone.
  const formatCheckoutDay = (date) => {
    const parsed = parseRentalDate(date);
    return parsed
      ? parsed.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })
      : "";
  };
  const startLabel = formatCheckoutDay(bookingData.startDate);
  const endLabel = formatCheckoutDay(bookingData.endDate);
  const rentalYear = parseRentalDate(bookingData.startDate)?.getFullYear();
  const dateRange =
    startLabel && endLabel && rentalYear
      ? `${startLabel} – ${endLabel}, ${rentalYear}`
      : `${quote.days} day(s)`;

  const transportSummary =
    bookingData.delivery && bookingData.homePickup
      ? `Delivery & pickup — ${bookingData.deliveryAddress}`
      : bookingData.delivery
        ? `Delivery to ${bookingData.deliveryAddress}`
        : bookingData.homePickup
          ? `Pickup from ${bookingData.deliveryAddress}`
          : "In-store pickup & return";

  const checkoutSubtitle = [
    [...bookingData.product1, ...bookingData.product2]
      .map((item) => `${item.quantity} × ${item.name}`)
      .join(", "),
    `${quote.days} day(s)`,
    transportSummary,
  ]
    .filter(Boolean)
    .join(" · ");

  // No card transaction for cash — the cash desk screen completes this booking.
  if (cashPayment) {
    return { paymentUrl: null, sessionId: null, bookingId, bookingData, pendingBookingsPath };
  }

  const createPaymentLink = httpsCallable(functions, "createPaymentLink");
  const paymentResult = await createPaymentLink({
    amountCents: quote.finalTotalCents,
    description: `Bike Rental — ${dateRange}`,
    subtitle: checkoutSubtitle,
    customerEmail: bookingData.customerEmail,
    customerPhone: bookingData.customerPhone,
    customerName: `${bookingData.firstName} ${bookingData.lastName}`.trim(),
    saveCard: true,
    bookingId,
    testMode: useTestStripe,
  });

  if (!paymentResult.data?.url) {
    throw new Error("Failed to create payment link");
  }

  return {
    paymentUrl: paymentResult.data.url,
    sessionId: paymentResult.data.sessionId,
    bookingId,
    bookingData,
    pendingBookingsPath,
  };
};
